/** @format */

import { ProductRentalURL } from './product-rental-url.interface';
import { ProductSkuCategoryDetails } from './product-sku-category-details.interface';
import { ProductSkuPrices } from './product-sku-prices.interface';
import { SkuDefiningAttribute } from './sku-defining-attribute.interface';
import { SkuDescriptiveAttribute } from './sku-descriptive-attribute.interface';

export interface ProductSku {
  /* Flag to denote whether the SKU is redirected in Akamai for given store ID. */
  akamaiRedirect: boolean;
  /* Auxiliary description of the product. */
  auxDescription1?: string;
  /* Additional auxiliary description of the product. */
  auxDescription2?: string;
  /* The type of badge to be displayed on the product card, i.e. TopSeller */
  badge?: string;
  /* Denotes whether the SKU/style is eligible for Buy Now. */
  buyNowEligible?: boolean;
  /* Denotes whether the SKU is eligible for BOPIS. */
  bopisFlag: boolean;
  /* BOPIS quantity available in selected store. */
  bopisQty?: number;
  /* Denotes whether the SKU is eligible for BOPL. */
  boplFlag: boolean;
  /* Denotes whether a sku is sellable or not. If 1, it is allowed to be sold. If 0, it is not. Check this value to determine showing add to cart or not. */
  buyable: 1 | 0;
  /* Details of the categories to which the SKU is related. */
  categories?: ProductSkuCategoryDetails[];
  catentryId: number;
  clearance: boolean;
  comingSoonEndDate?: string;
  definingAttributes?: SkuDefiningAttribute[];
  descriptiveAttributes: SkuDescriptiveAttribute[];
  /* The minimum number of characters required for a personalizable item's text. */
  emPersonalizedDigitsRequired?: number;
  fullImageURL?: string;
  /* Denotes whether a sku is sellable or not. Identical to `buyable` but a boolean instead of an integer. */
  isBuyable: boolean;
  isCustomizable?: boolean;
  isEMComingSoon?: boolean;
  isEcodePreSale: boolean;
  isGuaranteedToGetThere?: boolean;
  isHazmat?: boolean;
  isHotMarket?: boolean;
  isPayPalEligible: boolean;
  isPersonalizable?: boolean;
  /* Denotes whether an item's personalized text is of type `LINEAID`. */
  isPersonalizedLineAid?: boolean;
  /* Denotes whether an item's personalized text is NOT of type `LINEAID`. */
  isPersonalizedText?: boolean;
  /* Denotes whether an sku is eligible for in-store availability. */
  isaFlag: boolean;
  /* In-store availability quantity available in selected store. */
  isaQty?: number;
  /* Shipping lead time (in days). */
  leadTime?: number;
  longDescription?: string;
  /* Denotes whether SKU is in low stock or not. Only returned from the API when it is `true`. */
  lowStock?: boolean;
  name: string;
  /* Denotes whether SKU is unavailable & currently on order. */
  onOrder?: boolean;
  /* For SKU, this is its Style's `catentryId`. For style, this is its own `catentryId`. */
  parentCatentryId: number;
  /* For SKU, this is its Style's `partNumber`. For style, this is its own `partNumber`. */
  parentPartNumber: string;
  partNumber: string;
  /* Root-relative (e.g. `/p/...`) URL of the product. */
  pdpSeoUrl: string;
  /* Maximum character limit for personalizable products' text property. */
  personalizedCharLimit?: number;
  personalizedText?: string;
  /* Letter case the text should be for product if it is personalizable. */
  personalizedTextCase?: string;
  preSale: boolean;
  preSaleEndDate?: string;
  /* Date pre-sale starts for this product. */
  preSaleReleaseDate?: string;
  prices: ProductSkuPrices;
  /* SKU's primary category; this differs based on the store ID. */
  primaryCategory?: string;
  /* Generic description of what item the product/SKU is, e.g. "Sweatshirt" */
  productType?: string;
  quickViewEnabled?: boolean;
  ratingCount?: number;
  /* BazaarVoice rating value. */
  ratingValue?: number;
  /* SKU's rental URL; this differs based on the store ID. */
  rentalUrl?: ProductRentalURL;
  /* Denotes whether this product is raffle-eligible (for high demand products.) */
  reserved?: string;
  shipClass?: string;
  /* Ship-to-home quantity available. */
  shipQty: number;
  /* Denotes whether shipping is restricted for SKU. Is `true` for style if `true` for any SKU. */
  shippingRestricted: boolean;
  shortDescription?: string;
  sizeAndFit?: string[];
  swatchImageURL?: string;
  thumbnail?: string;
  /* Denotes whether this sku is eligible for having warranties added to cart with it */
  warrantyEligible?: boolean;
  webActiveDate: string;
}
